import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { 
  Card, 
  CardContent,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, Home, Users, User, Lock, Info, HelpCircle } from "lucide-react";
import { UserRole } from "@/lib/types";
import { useLocation } from "wouter";

// Simple registration schema with zod
const registerSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      "Password must include uppercase, lowercase, number and special character"
    ),
  confirmPassword: z.string(),
  fullName: z.string().min(2, "Full name is required"),
  phone: z.string().min(7, "Phone number is required"),
  role: z.nativeEnum(UserRole),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must agree to the terms and conditions"
  }),
  
  // Manager fields - conditionally required
  managerCompanyName: z.string().optional(),
  managerAddress: z.string().optional(),
  managerTaxId: z.string().optional(),
  managerTermsAccepted: z.boolean().optional(),
  
  // Agent fields - conditionally required
  agencyName: z.string().optional(),
  agencyAddress: z.string().optional(),
  agencyWebsite: z.string().optional(),
  agentTermsAccepted: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
}).refine(
  (data) => {
    if (data.role === UserRole.MANAGER) {
      return data.managerTermsAccepted === true;
    }
    return true;
  },
  {
    message: "You must agree to the Manager Agreement Terms",
    path: ["managerTermsAccepted"],
  }
).refine(
  (data) => {
    if (data.role === UserRole.AGENT) {
      return data.agentTermsAccepted === true;
    }
    return true;
  },
  {
    message: "You must agree to the Agent Agreement Terms",
    path: ["agentTermsAccepted"],
  }
).refine(
  (data) => {
    if (data.role === UserRole.MANAGER) {
      return !!data.managerCompanyName && data.managerCompanyName.length > 0;
    }
    return true;
  },
  {
    message: "Company name is required for managers",
    path: ["managerCompanyName"],
  }
).refine(
  (data) => {
    if (data.role === UserRole.AGENT) {
      return !!data.agencyName && data.agencyName.length > 0;
    }
    return true;
  },
  {
    message: "Agency name is required for agents",
    path: ["agencyName"],
  }
);

type RegisterFormValues = z.infer<typeof registerSchema>;

export function RegisterForm() {
  const { registerMutation } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.GUEST);
  const [location] = useLocation();
  
  // Initialize form with react-hook-form
  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      fullName: "",
      phone: "",
      role: selectedRole,
      termsAccepted: false,
    },
    mode: "onBlur",
  });

  // Effect for handling role changes
  useEffect(() => {
    // Reset form when role changes
    form.reset({
      ...form.getValues(),
      role: selectedRole,
    });
  }, [selectedRole, form]);

  // Set role from URL query parameter on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlRole = searchParams.get("role");
    if (urlRole) {
      // Accept both lowercase and uppercase
      const normalizedRole = urlRole.toUpperCase();
      if (Object.values(UserRole).includes(normalizedRole as UserRole)) {
        setSelectedRole(normalizedRole as UserRole);
        form.setValue("role", normalizedRole as UserRole);
      } else if (["guest", "agent", "manager"].includes(urlRole.toLowerCase())) {
        setSelectedRole(urlRole.toLowerCase() as UserRole);
        form.setValue("role", urlRole.toLowerCase() as UserRole);
      }
    }
  }, [form]);
  
  // Track selected role changes
  const onRoleChange = (value: UserRole) => {
    setSelectedRole(value);
    form.setValue("role", value);
    
    // Reset any role-specific fields when changing roles
    if (value === UserRole.MANAGER) {
      form.setValue("managerTermsAccepted", false);
    } else if (value === UserRole.AGENT) {
      form.setValue("agentTermsAccepted", false);
    }
  };

  // Form submission handler
  function onSubmit(data: RegisterFormValues) {
    // Get redirect param from URL if present
    const searchParams = new URLSearchParams(window.location.search);
    const redirectUrl = searchParams.get("redirect");
    registerMutation.mutate(data, {
      onSuccess: (userData) => {
        if (userData.role === UserRole.GUEST && redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          // fallback to default dashboard logic
          if (data.role === UserRole.MANAGER) {
            window.location.href = "/manager/dashboard";
          } else  if (data.role === UserRole.AGENT) {
            window.location.href = "/agent/dashboard";
          } else if (data.role === UserRole.ADMIN) {
            return "/properties";
          } else if (data.role === UserRole.GUEST) {
            return "/dashboard";
          }
        }
      }
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-sm font-medium leading-none">Select account type</h3>
          <p className="text-sm text-muted-foreground">
            Choose the type of account you want to create
          </p>
        </div>

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormControl>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* <div 
                    className={`cursor-pointer border-2 rounded-md ${selectedRole === UserRole.GUEST ? 'border-primary' : 'border-muted'}`}
                    onClick={() => onRoleChange(UserRole.GUEST)}
                  >
                    <div className="pt-4 pb-4 flex flex-col items-center text-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-medium">Guest</h4>
                        <p className="text-xs text-muted-foreground">
                          Book properties and track your reservations
                        </p>
                      </div>
                      <input 
                        type="radio" 
                        id="guest" 
                        name="role" 
                        value={UserRole.GUEST}
                        checked={selectedRole === UserRole.GUEST}
                        onChange={() => onRoleChange(UserRole.GUEST)} 
                        className="sr-only"
                        title="Guest account type"
                      />
                    </div>
                  </div> */}

                  <div 
                    className={`cursor-pointer border-2 rounded-md ${selectedRole === UserRole.AGENT ? 'border-primary' : 'border-muted'}`}
                    onClick={() => onRoleChange(UserRole.AGENT)}
                  >
                    <div className="pt-4 pb-4 flex flex-col items-center text-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-medium">Agent</h4>
                        <p className="text-xs text-muted-foreground">
                          Create booking links and earn commissions
                        </p>
                      </div>
                      <input 
                        type="radio" 
                        id="agent" 
                        name="role" 
                        value={UserRole.AGENT} 
                        checked={selectedRole === UserRole.AGENT}
                        onChange={() => onRoleChange(UserRole.AGENT)}
                        className="sr-only"
                        title="Agent account type"
                      />
                    </div>
                  </div>

                  <div 
                    className={`cursor-pointer border-2 rounded-md ${selectedRole === UserRole.MANAGER ? 'border-primary' : 'border-muted'}`}
                    onClick={() => onRoleChange(UserRole.MANAGER)}
                  >
                    <div className="pt-4 pb-4 flex flex-col items-center text-center space-y-2">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Home className="h-6 w-6 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-medium">Manager</h4>
                        <p className="text-xs text-muted-foreground">
                          List properties and manage bookings
                        </p>
                      </div>
                      <input 
                        type="radio" 
                        id="manager" 
                        name="role" 
                        value={UserRole.MANAGER}
                        checked={selectedRole === UserRole.MANAGER}
                        onChange={() => onRoleChange(UserRole.MANAGER)} 
                        className="sr-only"
                        title="Manager account type"
                      />
                    </div>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="johndoe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="john@example.com" 
                    autoComplete="email"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input placeholder="+971 5X XXX XXXX" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" />
                  Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      {...field}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                        >
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" x2="22" y1="2" y2="22" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                      <span className="sr-only">
                        {showPassword ? "Hide password" : "Show password"}
                      </span>
                    </Button>
                  </div>
                </FormControl>
                <FormDescription className="text-xs">
                  Must include uppercase, lowercase, number and special character
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" />
                  Confirm Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Manager-specific fields */}
        {selectedRole === UserRole.MANAGER && (
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium leading-none">Property Manager Details</h3>
              <p className="text-sm text-muted-foreground">
                Additional information required for property managers
              </p>
            </div>
            
            <FormField
              control={form.control}
              name="managerCompanyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your Company LLC" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="managerAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Business St, Dubai, UAE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="managerTaxId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax ID / Business Registration Number</FormLabel>
                  <FormControl>
                    <Input placeholder="123456789" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Agent-specific fields */}
        {selectedRole === UserRole.AGENT && (
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-sm font-medium leading-none">Rental Agent Details</h3>
              <p className="text-sm text-muted-foreground">
                Additional information required for rental agents
              </p>
            </div>
            
            <FormField
              control={form.control}
              name="agencyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your Agency Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="agencyAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Agency St, Dubai, UAE" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="agencyWebsite"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency Website (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="https://youragency.com" type="url" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Terms Acceptance */}
        <FormField
          control={form.control}
          name="termsAccepted"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </FormLabel>
                <FormDescription className="text-xs">
                  Required for all users
                </FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Manager-specific Terms */}
        {selectedRole === UserRole.MANAGER && (
          <FormField
            control={form.control}
            name="managerTermsAccepted"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 border-primary/20 bg-primary/5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I agree to the StayMiddle Property Manager Agreement Terms
                  </FormLabel>
                  <FormDescription className="text-xs">
                    <a
                      href="/manager-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      View the full Manager Agreement
                    </a>
                  </FormDescription>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Agent-specific Terms */}
        {selectedRole === UserRole.AGENT && (
          <FormField
            control={form.control}
            name="agentTermsAccepted"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 border-primary/20 bg-primary/5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I agree to the StayMiddle Agent Agreement Terms
                  </FormLabel>
                  <FormDescription className="text-xs">
                    <a
                      href="/agent-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      View the full Agent Agreement
                    </a>
                  </FormDescription>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="space-y-2 text-sm text-muted-foreground p-4 rounded-md bg-muted/30 border border-muted">
          {selectedRole === UserRole.MANAGER ? (
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Manager Registration:</strong> After registration, your account will require admin approval.
                Once approved, you'll be able to list properties, sync calendars, and maximize your occupancy.
                We'll also help you set up Stripe Connect for direct payments.
              </p>
            </div>
          ) : selectedRole === UserRole.AGENT ? (
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Agent Registration:</strong> After registration, your account will require admin approval.
                Once approved, you'll be able to create booking links with your own pricing and earn commissions through 
                our Stripe Connect integration.
              </p>
            </div>
          ) : (
            <></>
            // <div className="flex items-start gap-2">
            //   <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            //   <p>
            //     <strong>Guest Registration:</strong> Book premium properties through StayMiddle at exclusive rates.
            //     Your account will be activated immediately after registration.
            //   </p>
            // </div>
          )}
        </div>

        <Button 
          type="submit" 
          className="w-full"
          disabled={registerMutation.isPending}
        >
          {registerMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Account...
            </>
          ) : (
            "Create Account"
          )}
        </Button>
      </form>
    </Form>
  );
}